import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ReportsSubNav from "@/components/ReportsSubNav";
import ScopeToggle from "@/components/ScopeToggle";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { SkeletonTile, SkeletonRows } from "@/components/Skeleton";

const STATUS_COLOR = {
  on_track: "var(--cw-success)",
  approaching: "var(--cw-warning)",
  exceeded: "var(--cw-error)",
};
const STATUS_LABEL = {
  on_track: "On track",
  approaching: "Approaching",
  exceeded: "Exceeded",
};

export default function DealAging() {
  const { pipelineId } = usePipelineCtx();
  const [stageId, setStageId] = useState(null); // null = "All stages"
  const [scope, setScope] = useState("pipeline"); // v5.7 Addition 22
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (scope === "pipeline" && !pipelineId) return;
    const params = scope === "tenant" ? { scope: "tenant" } : { pipeline_id: pipelineId };
    if (scope === "pipeline" && stageId) params.stage_id = stageId;
    api.get("/reports/aging", { params }).then(({ data }) => setData(data));
  }, [pipelineId, stageId, scope]);

  return (
    <div className="p-8">
      <div className="cw-caption uppercase tracking-widest">Reporting</div>
      <div className="mt-1 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="cw-h1" data-testid="report-aging-title">Deal Aging</h1>
          {data && scope === "pipeline" && (
            <span
              className="cw-badge"
              style={{ background: "rgba(224,161,0,0.15)", color: "var(--cw-primary-dark)" }}
              data-testid="aging-threshold-pill"
            >
              &nbsp;
            </span>
          )}
        </div>
        <ScopeToggle value={scope} onChange={setScope} />
      </div>

      <div className="mt-4"><ReportsSubNav /></div>

      {!data ? (
        <div className="mt-6" data-testid="deal-aging-skeleton">
          <div className="grid grid-cols-3 gap-4">
            {[0,1,2].map(i => <SkeletonTile key={i} />)}
          </div>
          <div className="mt-6 cw-card p-5">
            <SkeletonRows rows={8} />
          </div>
        </div>
      ) : data.scope === "tenant" ? (
        // v5.7 Addition 22: tenant scope — flat rows across all pipelines
        <>
          <div className="cw-card mt-6 overflow-hidden" data-testid="aging-table">
            <table className="w-full text-sm">
              <thead style={{ background: "rgba(40,42,116,0.05)" }}>
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Pipeline</th>
                  <th className="px-4 py-3 font-semibold">Company</th>
                  <th className="px-4 py-3 font-semibold">Stage</th>
                  <th className="px-4 py-3 font-semibold">Days in Stage</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Owner</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-[var(--cw-muted)]">
                      No active deals in any pipeline.
                    </td>
                  </tr>
                )}
                {(data.rows || []).map((r) => (
                  <tr
                    key={r.deal_id}
                    className="border-t"
                    style={{ borderColor: "var(--cw-border)" }}
                    data-testid={`aging-row-${r.company_name}`}
                  >
                    <td className="px-4 py-3 text-[var(--cw-muted)]">{r.pipeline_name}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => nav(`/deals/${r.deal_id}`)}
                        className="text-left hover:underline"
                        style={{ color: "var(--cw-primary)" }}>
                        {r.company_name}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded"
                        style={{ background: "#F5F5F5", color: "var(--cw-body)" }}>
                        {r.stage_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold" style={{ color: STATUS_COLOR[r.status] }}>
                      {r.days_in_stage}d
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-medium"
                        style={{ color: STATUS_COLOR[r.status] }}
                      >
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--cw-muted)]">{r.owner_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* Stage tabs */}
          <div className="mt-6 border-b flex gap-1 overflow-x-auto"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="aging-stage-tabs">
            <TabBtn active={stageId === null} onClick={() => setStageId(null)} testid="aging-tab-all">
              All stages
            </TabBtn>
            {(data.tabs || []).map((t) => (
              <TabBtn key={t.stage_id}
                active={stageId === t.stage_id}
                onClick={() => setStageId(t.stage_id)}
                testid={`aging-tab-${t.stage_name}`}>
                {t.stage_name} <span className="text-xs text-[var(--cw-muted)] ml-1">({t.count})</span>
              </TabBtn>
            ))}
          </div>



          {/* KPI tiles */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <AgingTile label="On Track" value={data.on_track}
              sub={`< ${data.approaching_boundary} days in stage`} color="var(--cw-success)"
              testid="aging-tile-on-track" />
            <AgingTile label="Approaching" value={data.approaching}
              sub={`>= ${data.approaching_boundary} days in stage`} color="var(--cw-warning)"
              testid="aging-tile-approaching" />
            <AgingTile label="Exceeded Threshold" value={data.exceeded}
              sub={`> ${data.banner_threshold} days in stage`} color="var(--cw-error)"
              testid="aging-tile-exceeded" />
          </div>

          {/* Table */}
          <div className="cw-card mt-6 overflow-hidden" data-testid="aging-table">
            <table className="w-full text-sm">
              <thead style={{ background: "rgba(40,42,116,0.05)" }}>
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Company</th>
                  <th className="px-4 py-3 font-semibold">Stage</th>
                  <th className="px-4 py-3 font-semibold">Days in Stage</th>
                  <th className="px-4 py-3 font-semibold w-40">Progress</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Owner</th>
                  <th className="px-4 py-3 font-semibold">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-[var(--cw-muted)]">
                      No active deals in this view.
                    </td>
                  </tr>
                )}
                {data.rows.map((r) => (
                  <tr key={r.deal_id} className="border-t"
                    style={{ borderColor: "var(--cw-border)" }}
                    data-testid={`aging-row-${r.company_name}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => nav(`/deals/${r.deal_id}`)}
                        className="text-left hover:underline"
                        style={{ color: "var(--cw-primary)" }}>
                        {r.company_name}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded"
                        style={{ background: "#F5F5F5", color: "var(--cw-body)" }}>
                        {r.stage_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold" style={{ color: STATUS_COLOR[r.status] }}>
                      {r.days_in_stage}d
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-1.5 rounded" style={{ background: "#F5F5F5" }}>
                        <div className="h-full rounded" style={{
                          width: `${r.progress_pct}%`,
                          background: STATUS_COLOR[r.status],
                          transition: "width 250ms ease",
                        }} />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="cw-badge" style={{
                        background: STATUS_COLOR[r.status],
                        color: r.status === "approaching" ? "var(--cw-primary-dark)" : "white",
                      }}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.owner_name ? (
                        <span className="text-[var(--cw-body)]">{r.owner_name}</span>
                      ) : (
                        <span style={{ color: "var(--cw-error)" }}>Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[var(--cw-muted)] text-xs">
                      {r.last_activity_at
                        ? r.last_activity_days_ago === 0
                          ? "Today"
                          : `${r.last_activity_days_ago}d ago`
                        : "No activity"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="cw-caption p-3 border-t" style={{ borderColor: "var(--cw-border)" }}>
              Sorted by longest time in current stage. Days calculated from when each deal last
              moved stages. Threshold per stage is set in Pipeline Configuration.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
        active ? "" : "text-[var(--cw-muted)] border-transparent"
      }`}
      style={active ? { color: "var(--cw-primary)", borderColor: "var(--cw-primary)" } : {}}
      data-testid={testid}>
      {children}
    </button>
  );
}

function AgingTile({ label, value, sub, color, testid }) {
  return (
    <div className="cw-card p-4" data-testid={testid}>
      <div className="text-xs" style={{ color: "var(--cw-muted)" }}>{label}</div>
      <div className="text-[28px] font-bold mt-1" style={{ color }}>{value}</div>
      <div className="text-xs mt-1" style={{ color }}>{sub}</div>
    </div>
  );
}
